import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { getGeckos } from "@/lib/site";
import { Reveal, SectionTag } from "@/components/Reveal";
import { useCart } from "@/context/CartContext";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const BASE_OPTIONS = [
  ["alle", "Alle"],
  ["dark", "Mørk / Sort"],
  ["red", "Rød"],
  ["yellow", "Gul"],
  ["orange", "Orange"],
  ["lavender", "Oliven / Lavender"],
  ["buckskin", "Bukkeskind (Buckskin)"],
];
const TRAIT_OPTIONS = ["alle", "Lillywhite", "Frappuccino", "Sable", "Phantom"];
const PATTERN_OPTIONS = [
  ["alle", "Alle"],
  ["patternless", "Patternless (Ensfarvet)"],
  ["flame", "Flame"],
  ["harlequin", "Harlequin / Extreme"],
  ["pinstripe", "Pinstripe / Quadstripe"],
  ["tiger", "Tiger / Brindle"],
  ["dalmatiner", "Dalmatiner / Super Dalmatiner"],
  ["halloween", "Halloween"],
  ["creamsicle", "Creamsicle"],
];
const PATTERN_MATCH = {
  patternless: ["patternless", "ensfarvet"],
  flame: ["flame"],
  harlequin: ["harlequin", "extreme"],
  pinstripe: ["pinstripe", "quadstripe"],
  tiger: ["tiger", "brindle"],
  dalmatiner: ["dalmatiner", "super dalmatiner"],
  halloween: ["halloween"],
  creamsicle: ["creamsicle"],
};
const LINE_OPTIONS = ["alle", "Cappuccino", "Axanthic", "Tri-color", "Confetti Dalmatian"];

function FilterGroup({ label, options, value, onChange, testid }) {
  return (
    <div>
      <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-pale/70">
        {label}
      </p>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.map((opt) => {
          const [val, text] = Array.isArray(opt) ? opt : [opt, opt === "alle" ? "Alle" : opt];
          const active = value === val;
          return (
            <button
              key={val}
              data-testid={`${testid}-${slugify(val)}`}
              onClick={() => onChange(val)}
              className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                active
                  ? "border-forest bg-forest text-cream"
                  : "border-white/15 text-sage hover:border-white/40 hover:text-cream"
              }`}
            >
              {text}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MorphCard({ gecko, featured, bento }) {
  const { add } = useCart();
  const navigate = useNavigate();
  const soldOut = gecko.status === "Udsolgt" || (gecko.inventory_qty ?? 1) <= 0;
  const available = !soldOut;
  const onAction = (e) => {
    e.stopPropagation();
    add({
      slug: gecko.slug,
      name: gecko.name,
      price_dkk: gecko.price_dkk,
      image: gecko.image,
    });
  };
  return (
    <div
      data-testid={`morph-card-${slugify(gecko.name)}`}
      onClick={() => navigate(`/vare/${gecko.slug}`)}
      className={`group relative cursor-pointer overflow-hidden border border-white/10 ${
        featured ? "lg:col-span-4 lg:row-span-2" : bento ? "lg:col-span-2" : ""
      }`}
    >
      <img
        src={gecko.image}
        alt={gecko.name}
        style={{ objectPosition: gecko.object_position }}
        className={`absolute inset-0 h-full w-full object-cover transition-[opacity,transform] duration-700 group-hover:scale-105 ${
          soldOut ? "opacity-30" : "opacity-90 group-hover:opacity-100"
        }`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
      <span className="absolute left-5 top-5 border border-white/20 bg-ink/50 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-pale backdrop-blur-md">
        {soldOut ? "Udsolgt" : gecko.status}
      </span>
      {soldOut && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            data-testid={`morph-soldout-overlay-${slugify(gecko.name)}`}
            className="border border-bark/60 bg-ink/70 px-6 py-2 font-mono text-xs uppercase tracking-[0.3em] text-bark backdrop-blur-md"
          >
            Udsolgt
          </span>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 p-6">
        <h3 className={`font-serif tracking-tight text-cream ${featured ? "text-4xl" : "text-2xl"}`}>
          {gecko.name}
        </h3>
        <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-pale">
          {gecko.price}
        </p>
        {featured && (
          <p className="mt-3 max-w-md text-sm leading-relaxed text-sage line-clamp-3">
            {gecko.description}
          </p>
        )}
        {available ? (
          <button
            data-testid={`morph-action-${slugify(gecko.name)}`}
            onClick={onAction}
            className="mt-4 flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-pale hover:text-ink"
          >
            <ShoppingBag className="h-3.5 w-3.5" />
            Læg i kurv
          </button>
        ) : (
          <p
            data-testid={`morph-soldout-${slugify(gecko.name)}`}
            className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-sage"
          >
            Udsolgt
          </p>
        )}
      </div>
    </div>
  );
}

export default function Morphs() {
  const [geckos, setGeckos] = useState([]);
  const [base, setBase] = useState("alle");
  const [trait, setTrait] = useState("alle");
  const [pattern, setPattern] = useState("alle");
  const [line, setLine] = useState("alle");

  useEffect(() => {
    getGeckos().then(setGeckos).catch(() => {});
  }, []);

  const filtering = base !== "alle" || trait !== "alle" || pattern !== "alle" || line !== "alle";
  const visible = geckos.filter((g) => {
    const patternOk =
      pattern === "alle" ||
      (PATTERN_MATCH[pattern] || []).includes((g.pattern || "").toLowerCase());
    return (
      (base === "alle" || g.base_color === base) &&
      (trait === "alle" || (g.traits || []).includes(trait)) &&
      patternOk &&
      (line === "alle" || (g.line || "") === line)
    );
  });

  return (
    <section id="morphs" data-testid="morphs-section" className="px-6 py-24 lg:px-16 lg:py-36">
      <Reveal>
        <SectionTag index="01" label="Samlingen" />
        <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
          <h2 className="max-w-xl font-serif text-4xl tracking-tight text-cream sm:text-5xl">
            Correlophus ciliatus
          </h2>
          <p className="max-w-xs font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-sage/70">
            Levende dyr sælges som privat hobbyopdræt af Frederik &amp; Sesilie
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="mt-12 border border-white/10 bg-moss p-6 lg:p-8">
          <div className="grid gap-8 lg:grid-cols-4">
            <FilterGroup label="Grundfarve" options={BASE_OPTIONS} value={base} onChange={setBase} testid="filter-base" />
            <FilterGroup label="Genetik & Morfer" options={TRAIT_OPTIONS} value={trait} onChange={setTrait} testid="filter-trait" />
            <FilterGroup label="Mønstertyper & Kombinationer" options={PATTERN_OPTIONS} value={pattern} onChange={setPattern} testid="filter-pattern" />
            <FilterGroup label="GEKKOFUEL linjer:" options={LINE_OPTIONS} value={line} onChange={setLine} testid="filter-line" />
          </div>
          <p data-testid="filter-result-count" className="mt-6 border-t border-white/10 pt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-sage/70">
            {visible.length} {visible.length === 1 ? "morf" : "morfer"} fundet
          </p>
        </div>
      </Reveal>

      {visible.length === 0 ? (
        <p data-testid="filter-empty" className="mt-14 text-sage">
          Ingen morfer matcher filtrene — prøv at udvide søgningen.
        </p>
      ) : (
        <div
          className={`mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 ${
            filtering ? "auto-rows-[380px] lg:grid-cols-3" : "auto-rows-[300px] lg:grid-cols-6"
          }`}
        >
          {visible.map((g) => (
            <MorphCard
              key={g.id}
              gecko={g}
              featured={!filtering && g.featured}
              bento={!filtering}
            />
          ))}
        </div>
      )}
    </section>
  );
}
